import { useEffect, useRef, useCallback, useState } from 'react';
import { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

interface UseTodoSubscriptionProps {
  roomId: string;
  listIds: string[];
  onUpdate: (lists: any[]) => void;
}

export function useTodoSubscription({ roomId, listIds = [], onUpdate }: UseTodoSubscriptionProps) {
  const channelRef = useRef<RealtimeChannel | null>(null);
  const isMounted = useRef(true);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(0); // Track last update time
  const refreshCountRef = useRef(0);

  // Function to fetch the latest lists
  const fetchLists = useCallback(async () => {
    if (!isMounted.current) return;
    
    try {
      console.log('Fetching latest todo lists...');
      const { data, error } = await supabase
        .from('todo_lists_with_tasks')
        .select('*')
        .eq('room_id', roomId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      if (data) {
        console.log(`Fetched ${data.length} todo lists`);
        onUpdate(data);
      }
    } catch (error) {
      console.error('Error fetching todo lists:', error);
    }
  }, [roomId, onUpdate]);

  // Setup the realtime subscription
  const setupSubscription = useCallback(async () => {
    if (!isMounted.current || !roomId) {
      console.log('Skipping subscription setup - missing roomId or unmounted');
      return;
    }
    
    console.log('🔌 Setting up todo subscription for room:', roomId);
    
    // Clean up existing subscription if any
    if (channelRef.current) {
      console.log('🧹 Cleaning up existing subscription');
      try {
        console.log('Current subscription status: cleaning up');
        await channelRef.current.unsubscribe();
        console.log('Successfully unsubscribed from previous channel');
      } catch (error) {
        console.error('❌ Error unsubscribing from channel:', error);
      } finally {
        channelRef.current = null;
      }
    }

    try {
      // Create a new channel with a unique name
      const channelName = `room_${roomId}_todo_${Date.now()}`;
      console.log(`📡 Creating new channel: ${channelName}`);
      
      const channel = supabase.channel(channelName, {
        config: {
          broadcast: { self: false }, // Don't receive own broadcasts
          presence: { key: channelName }
        }
      });
      
      // Debug logging for channel events
      channel.on('broadcast', { event: '*' }, (payload) => {
        console.log('📢 Channel broadcast:', payload);
      });
      
      // System event handler with proper typing
      channel.on('system' as any, {
        event: '*'
      }, (event: any) => {
        console.log('🔧 System event:', event);
        
        // Handle reconnection events
        if (event.status === 'reconnected') {
          console.log('🔄 Reconnected to realtime server, refreshing data...');
          fetchLists();
        }
      });
      
      // Skip presence tracking for now as it's causing type issues
      console.log('ℹ️  Presence tracking is currently disabled due to TypeScript type issues');
      
      // Subscribe to database changes
      const subscriptions = [
        {
          event: '*',
          schema: 'public',
          table: 'todo_lists',
          filter: `room_id=eq.${roomId}`,
          id: 'todo-lists-subscription',
          required: true
        },
        {
          event: '*',
          schema: 'public',
          table: 'todo_tasks',
          // tasks don't include room_id; listen to all and filter client-side
          filter: undefined,
          id: 'todo-tasks-subscription',
          required: true
        }
      ];
      
      // Add all subscriptions
      subscriptions.forEach(({ event, schema, table, filter, id, required = true }) => {
        if (!table) return;
        
        console.log(`🔔 Subscribing to ${table} changes`, { filter });
        
        try {
          const subscription = channel.on('postgres_changes' as any, 
            { event, schema, table, filter },
            (payload: any) => {
              const now = new Date().toISOString();
              console.log(`🔄 [${now}] ${table} change detected (${event}):`, payload);
              
              // Immediately update the UI with the new data
              fetchLists().then(() => {
                console.log(`✅ [${now}] Successfully refreshed ${table} data after change`);
              }).catch(error => {
                console.error(`❌ [${now}] Error refreshing ${table} data:`, error);
              });
              
              // Force a state update to ensure UI re-renders
              setLastUpdate(Date.now());
            }
          );
          
          // Store subscription reference if needed
          (subscription as any).id = id;
          console.log(`✅ Successfully subscribed to ${table} changes`);
        } catch (error) {
          const errorMessage = `⚠️ Failed to subscribe to ${table} changes`;
          console.error(errorMessage, error);
          
          if (required) {
            // Only throw for required subscriptions
            throw new Error(`${errorMessage}: ${error instanceof Error ? error.message : String(error)}`);
          }
          
          // For non-required subscriptions, just log the error
          console.warn(`Proceeding without ${table} subscription as it's not required`);
        }
      });
      
      // Subscribe to the channel
      const subscription = channel.subscribe((status: string, err?: Error) => {
        console.log(`📡 Subscription status changed to: ${status}`);
        
        if (err) {
          console.error('❌ Subscription error:', err);
          return;
        }
        
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to realtime changes');
          // Initial fetch after subscription is established
          fetchLists();
        }
        
        if (status === 'CHANNEL_ERROR') {
          console.error('❌ Channel error - check Supabase dashboard for issues');
        }
        
        if (status === 'TIMED_OUT') {
          console.error('⌛ Channel connection timed out - attempting to resubscribe...');
          // Attempt to resubscribe after a delay
          setTimeout(() => setupSubscription(), 2000);
        }
      });
      
      // Track the channel
      channelRef.current = channel;
      
      // Set up a heartbeat to monitor the connection
      const heartbeat = setInterval(() => {
        if (!channelRef.current) return;
        console.log('💓 Channel heartbeat');
      }, 30000);
      
      // Clean up heartbeat on unmount
      return () => {
        clearInterval(heartbeat);
      };
      
    } catch (error) {
      console.error('❌ Failed to set up subscription:', error);
      // Attempt to resubscribe after an error
      setTimeout(() => setupSubscription(), 5000);
    }
    
    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe().catch(console.error);
        channelRef.current = null;
      }
    };
  }, [roomId, fetchLists]);

  // Setup and cleanup
  useEffect(() => {
    if (!roomId) return;
    
    isMounted.current = true;
    
    // Initial setup
    setupSubscription();
    
    // Cleanup on unmount
    return () => {
      isMounted.current = false;
      if (channelRef.current) {
        channelRef.current.unsubscribe().catch(console.error);
        channelRef.current = null;
      }
    };
  }, [setupSubscription, roomId]);

  // Manual refresh function
  const refresh = useCallback(() => {
    refreshCountRef.current += 1;
    console.log('Manual refresh triggered:', refreshCountRef.current);
    fetchLists();
  }, [fetchLists]);

  return { refresh };
}
